import React, { useState, useEffect } from 'react';
import { X, Search, CheckSquare, Bell, FileText, Brain, BookOpen, Loader2, MessageSquare } from 'lucide-react';
import api from '../../utils/api';

const ItemSelectorModal = ({ isOpen, onClose, onSelect, itemType, courseType }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        if (isOpen && itemType) {
            fetchItems();
            setSearchQuery('');
            setSelectedItem(null);
        }
    }, [isOpen, itemType, courseType]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            let endpoint = '';
            let params = { phase: courseType }; // Filter by course type (preboarding/onboarding)

            if (itemType === 'task') {
                endpoint = '/tasks';
                params.status = 'published'; // Only show published tasks
            } else if (itemType === 'notice') {
                endpoint = '/notices';
                params.status = 'published';
            } else if (itemType === 'survey') {
                endpoint = '/surveys';
                params.status = 'active'; // Only show active surveys
            }

            const response = await api.get(endpoint, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            setItems(response.data.tasks || response.data.notices || response.data.surveys || []);
        } catch (error) {
            console.error('Error fetching items:', error);
        } finally {
            setLoading(false);
        }
    };

    const getItemIcon = (item) => {
        if (itemType === 'notice') {
            return <Bell className="w-5 h-5 text-yellow-600" />;
        }

        if (itemType === 'survey') {
            return <MessageSquare className="w-5 h-5 text-green-600" />;
        }

        // For tasks, use task_type
        switch (item.task_type) {
            case 'quiz':
                return <Brain className="w-5 h-5 text-blue-600" />;
            case 'training':
                return <BookOpen className="w-5 h-5 text-blue-600" />;
            case 'checklist':
                return <CheckSquare className="w-5 h-5 text-blue-600" />;
            default:
                return <FileText className="w-5 h-5 text-blue-600" />;
        }
    };

    const getTypeBadge = (item) => {
        if (itemType === 'notice') {
            return (
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded">
                    Notice
                </span>
            );
        }

        if (itemType === 'survey') {
            return (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                    Survey
                </span>
            );
        }

        const taskTypeLabels = {
            quiz: 'Quiz',
            training: 'Training',
            checklist: 'Checklist',
            task: 'Task'
        };

        return (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                {taskTypeLabels[item.task_type] || 'Task'}
            </span>
        );
    };

    const filteredItems = items.filter(item =>
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleConfirm = () => {
        if (selectedItem) {
            onSelect(selectedItem);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">
                            Select {itemType === 'task' ? 'Task' : 'Notice'}
                        </h2>
                        <p className="text-sm text-gray-600">
                            Choose an existing {itemType} to add to the timeline
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search */}
                <div className="px-6 py-3 border-b border-gray-100">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder={`Search ${itemType === 'task' ? 'tasks' : 'notices'}...`}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Items List */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500">
                                {searchQuery
                                    ? `No ${itemType === 'task' ? 'tasks' : 'notices'} found matching "${searchQuery}"`
                                    : `No published ${itemType === 'task' ? 'tasks' : 'notices'} available for ${courseType}`
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredItems.map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => setSelectedItem(item)}
                                    className={`
                                        w-full text-left p-4 rounded-xl border-2 transition-all
                                        ${selectedItem?.id === item.id
                                            ? 'border-primary bg-primary/5'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }
                                    `}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`p-2 rounded-lg ${itemType === 'notice' ? 'bg-yellow-50' : 'bg-blue-50'}`}>
                                            {getItemIcon(item)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h4 className="font-semibold text-gray-800">{item.title}</h4>
                                                {getTypeBadge(item)}
                                            </div>
                                            {item.description && (
                                                <p className="text-sm text-gray-600 line-clamp-2">
                                                    {item.description}
                                                </p>
                                            )}
                                            {item.assigned_roles && item.assigned_roles.length > 0 && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Assigned to: {item.assigned_roles.join(', ')}
                                                </p>
                                            )}
                                        </div>
                                        {selectedItem?.id === item.id && (
                                            <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!selectedItem}
                        className={`
                            px-5 py-2 rounded-lg font-medium transition-colors
                            ${selectedItem
                                ? 'bg-primary hover:bg-primary-hover text-white'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }
                        `}
                    >
                        Add to Timeline
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ItemSelectorModal;
